import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  useEffect(() => {
    // Ensure the window object exists (e.g., during SSR)
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/4c54787fd2047152d108467e5113439e/e5fe30b4f26c08cfb369c5b19d957793.png"
              alt="Sri Maruthi Teeth Empire Logo"
              className="w-10 h-10 object-contain rounded-lg"
            />
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Sri Maruthi Teeth Empire
            </h1>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-gray-300 hover:text-teal-400 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-teal-600 to-teal-500 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-5">
            <i className="ri-shield-check-line text-3xl"></i>
          </div>
          <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Privacy Policy
          </h1>
          <p className="text-teal-100 text-sm">Last updated: January 2025</p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <article className="space-y-10">
          {/* Introduction */}
          <section>
            <p className="text-gray-600 text-sm leading-relaxed">
              At <strong>Sri Maruthi Teeth Empire</strong>, located at Botanical Garden Road, Kondapur, Hyderabad,
              Telangana, we are committed to protecting your privacy and ensuring the security of
              your personal information. This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you visit our website or use our dental
              clinic services. Please read this policy carefully to understand our practices
              regarding your personal data.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#information-collect" id="information-collect" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  1
                </span>
                Information We Collect
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <div>
                <h5 className="text-sm font-semibold text-slate-800 mb-2">Personal Information</h5>
                <p className="text-gray-600 text-sm leading-relaxed">
                  When you book an appointment, submit a contact form, or interact with our services,
                  we may collect the following personal information:
                </p>
                <ul className="mt-2 space-y-1.5 text-gray-600 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-500 mt-0.5"></i>
                    Full name
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-500 mt-0.5"></i>
                    Email address
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-500 mt-0.5"></i>
                    Phone number
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-500 mt-0.5"></i>
                    Preferred appointment date and time
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-500 mt-0.5"></i>
                    Dental service preferences and treatment history
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-500 mt-0.5"></i>
                    Any messages or inquiries you submit through our forms
                  </li>
                </ul>
              </div>
              <div>
                <h5 className="text-sm font-semibold text-slate-800 mb-2">
                  Automatically Collected Information
                </h5>
                <p className="text-gray-600 text-sm leading-relaxed">
                  When you visit our website, we may automatically collect certain information
                  including your IP address, browser type, operating system, referring URLs, pages
                  viewed, and the dates and times of your visits. This data helps us improve our
                  website experience and dental services in Kondapur, Hyderabad.
                </p>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#how-we-use" id="how-we-use" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  2
                </span>
                How We Use Your Information
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                Sri Maruthi Teeth Empire uses the collected information for the following purposes:
              </p>
              <ul className="space-y-2.5 text-gray-600 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                    a
                  </span>
                  <span>To schedule and manage your dental appointments at our Kondapur clinic</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                    b
                  </span>
                  <span>To respond to your inquiries, questions, and requests sent via email or WhatsApp</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                    c
                  </span>
                  <span>To send appointment reminders and follow-up communications</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                    d
                  </span>
                  <span>To improve our dental services, website functionality, and patient experience</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                    e
                  </span>
                  <span>To comply with legal obligations and protect our rights</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                    f
                  </span>
                  <span>To send promotional offers about our dental treatments (only with your consent)</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Data Sharing */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#data-sharing" id="data-sharing" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  3
                </span>
                Data Sharing &amp; Disclosure
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                We do <strong>not sell, trade, or rent</strong> your personal information to third parties.
                We may share your information only in the following circumstances:
              </p>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-lock-line text-teal-500 mt-0.5"></i>
                  <span>
                    <strong>Service Providers:</strong> Trusted third-party service providers who assist
                    us in operating our website and conducting our dental practice, subject to confidentiality
                    agreements.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-lock-line text-teal-500 mt-0.5"></i>
                  <span>
                    <strong>Legal Requirements:</strong> When required by law, regulation, or legal process,
                    or to protect the rights, property, or safety of Sri Maruthi Teeth Empire, our patients, or others.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-lock-line text-teal-500 mt-0.5"></i>
                  <span>
                    <strong>Consent:</strong> With your explicit consent for any other purpose not described in
                    this policy.
                  </span>
                </li>
              </ul>
            </div>
          </section>

          {/* Data Security */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#data-security" id="data-security" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  4
                </span>
                Data Security
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your
                personal information against unauthorized access, alteration, disclosure, or destruction.
                These measures include encrypted data transmission, secure servers, and restricted access to
                personal data. However, no method of transmission over the Internet or electronic storage
                is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>
          </section>

          {/* Cookies */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#cookies" id="cookies" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  5
                </span>
                Cookies &amp; Tracking Technologies
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                Our website may use cookies and similar tracking technologies to enhance your browsing
                experience. Cookies are small data files stored on your device that help us understand how
                you use our website.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed">
                You can control cookie preferences through your browser settings. Disabling cookies may
                affect certain features of our website, such as appointment booking functionality. We use
                cookies for analytics purposes to improve our dental clinic services in Kondapur, Hyderabad.
              </p>
            </div>
          </section>

          {/* Third-Party Links */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#third-party" id="third-party" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  6
                </span>
                Third-Party Links
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed">
                Our website may contain links to third-party websites, including Google Maps, WhatsApp,
                and social media platforms. We are not responsible for the privacy practices or content
                of these external sites. We encourage you to review the privacy policies of any
                third‑party websites you visit through links on our site.
              </p>
            </div>
          </section>

          {/* Your Rights */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#your-rights" id="your-rights" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  7
                </span>
                Your Rights
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                You have the following rights regarding your personal data:
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                  <i className="ri-user-settings-line text-teal-500 mt-0.5"></i>
                  <span className="text-gray-600 text-sm">
                    <strong>Access:</strong> Request a copy of your personal data we hold
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-edit-line text-teal-500 mt-0.5"></i>
                  <span className="text-gray-600 text-sm">
                    <strong>Correction:</strong> Request correction of inaccurate or incomplete data
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-delete-bin-line text-teal-500 mt-0.5"></i>
                  <span className="text-gray-600 text-sm">
                    <strong>Deletion:</strong> Request deletion of your personal data
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-forbid-line text-teal-500 mt-0.5"></i>
                  <span className="text-gray-600 text-sm">
                    <strong>Objection:</strong> Object to processing of your personal data
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-download-line text-teal-500 mt-0.5"></i>
                  <span className="text-gray-600 text-sm">
                    <strong>Portability:</strong> Request transfer of your data in a portable format
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-teal-500 mt-0.5"></i>
                  <span className="text-gray-600 text-sm">
                    <strong>Withdraw Consent:</strong> Withdraw consent at any time for marketing communications
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Children's Privacy */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#childrens-privacy" id="childrens-privacy" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  8
                </span>
                Children&apos;s Privacy
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed">
                Our website is not directed at children under the age of 13. We do not knowingly collect
                personal information from children. For pediatric dental appointments at Sri Maruthi Teeth Empire, a
                parent or guardian must provide consent and submit information on behalf of the child.
                If we become aware that we have collected data from a child without parental consent,
                we will take steps to delete that information promptly.
              </p>
            </div>
          </section>

          {/* Changes to Policy */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#changes" id="changes" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  9
                </span>
                Changes to This Policy
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices
                or for other operational, legal, or regulatory reasons. Any changes will be posted on
                this page with an updated "Last updated" date. We encourage you to review this policy
                periodically to stay informed about how we protect your information.
              </p>
            </div>
          </section>

          {/* Contact Us */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#contact-us" id="contact-us" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  10
                </span>
                Contact Us
              </a>
            </h4>
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our
                data practices, please contact us:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg">
                    <i className="ri-building-2-line text-sm"></i>
                  </span>
                  <span className="text-gray-700 text-sm font-medium">Sri Maruthi Teeth Empire Dental Clinic</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg">
                    <i className="ri-map-pin-line text-sm"></i>
                  </span>
                  <span className="text-gray-700 text-sm">
                    Botanical Garden Road, Kondapur, Hyderabad, Telangana 500084
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg">
                    <i className="ri-mail-line text-sm"></i>
                  </span>
                  <a
                    href="mailto:teethempire08@gmail.com"
                    className="text-teal-600 hover:text-teal-700 text-sm font-medium transition-colors cursor-pointer"
                  >
                    teethempire08@gmail.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg">
                    <i className="ri-phone-line text-sm"></i>
                  </span>
                  <a
                    href="tel:+918340978930"
                    className="text-teal-600 hover:text-teal-700 text-sm font-medium transition-colors cursor-pointer"
                  >
                    +91 83409 78930
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-green-100 text-green-600 rounded-lg">
                    <i className="ri-whatsapp-line text-sm"></i>
                  </span>
                  <a
                    href="https://wa.me/918340978930"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-green-600 hover:text-green-700 text-sm font-medium transition-colors cursor-pointer"
                  >
                    WhatsApp: +91 83409 78930
                  </a>
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">© 2025 Sri Maruthi Teeth Empire. All rights reserved.</p>
          <Link
            to="/"
            className="text-gray-400 hover:text-teal-400 text-sm transition-colors cursor-pointer"
          >
            Back to Home
          </Link>
        </div>
      </footer>
    </div>
  );
}
